import React, { FC, useCallback, useEffect, useState } from 'react';
import { WebUxTitle } from '../WebUxContext';
import { Button, Table, Tag } from 'antd';
import { Site } from '../../dto';
import { useConnection } from '../../connection/connection';
import { useBusinessContext } from '../../context/BusinessContext';
import SiteCreateModal, { useSiteCreateModal } from './SiteCreateModal';
import { useEventListener } from '../../common/EventContext';
import { EditOutlined } from '@ant-design/icons';

type Props = {};

export const SitesPage: FC<Props> = (props) => {
  const [sites, setSites] = useState<Site[]>([]);
  const connection = useConnection();
  const business = useBusinessContext();
  const createModal = useSiteCreateModal();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setLoading(true);
    connection
      .get(`sites/${business.businessId}/all`)
      .then((res) => {
        setSites(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [business.businessId, connection]);

  useEventListener(
    'SITE',
    useCallback((evt) => {
      if (evt.action === 'CREATE') {
        setSites((prevState) => [...prevState, evt.data]);
      } else if (evt.action === 'UPDATE') {
        setSites((prevState) =>
          prevState.map((s) => (s.siteId === evt.data.siteId ? evt.data : s)),
        );
      } else {
        setSites((prevState) =>
          prevState.filter((s) => s.siteId !== evt.data.siteId),
        );
      }
    }, []),
  );

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        if (!status) return <div />;
        const color = status?.toLowerCase() === 'active' ? 'green' : 'red';
        return (
          <Tag color={color} key={status}>
            {status?.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Action',
      key: 'action',
      render: (text: string, record: Site) => (
        <EditOutlined onClick={() => createModal.current?.edit(record)} />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-2">
      <WebUxTitle>Sites</WebUxTitle>
      <div className="flex justify-end">
        <Button
          type="primary"
          onClick={() => {
            createModal.current?.create();
          }}
        >
          Create a new Site
        </Button>
      </div>
      <Table
        loading={loading}
        dataSource={sites}
        columns={columns}
        rowKey="siteId"
      />
      <SiteCreateModal ref={createModal} />
    </div>
  );
};
